/**
 * Moving focus from script without flashing a focus ring at touch and mouse users.
 *
 * The drawer has to move focus when it opens and closes, for keyboard and screen reader users.
 * Browsers decide for themselves whether a script-driven focus matches `:focus-visible`, and iOS
 * Safari says yes after a tap: taps never focus links or buttons there, so focus arrives "from
 * nowhere" and gets the ring. The Webflow focus styles are correct; the browser's guess is not.
 *
 * So we keep track of how the person last interacted. After a key press the ring is left alone.
 * After a pointer press the outline is switched off inline for as long as that element keeps
 * focus, and handed back to the stylesheet on blur, so a later Tab shows the ring as usual.
 */

let keyboard = false
let listening = false

function listen() {
  if (listening) return
  listening = true
  // Capture phase, so these run before any handler that goes on to move focus
  addEventListener('keydown', (event) => {
    if (!event.metaKey && !event.ctrlKey && !event.altKey) keyboard = true
  }, true)
  addEventListener('pointerdown', () => (keyboard = false), true)
  addEventListener('touchstart', () => (keyboard = false), { capture: true, passive: true })
}

/** Call once at startup so the first interaction is already being tracked. */
export function trackInputModality() {
  listen()
}

/** `element.focus()`, with the ring shown only if the person is navigating by keyboard. */
export function focusQuietly(element: HTMLElement) {
  listen()
  if (keyboard) {
    element.focus()
    return
  }
  const previous = element.style.outline
  element.style.outline = 'none'
  element.addEventListener('blur', () => (element.style.outline = previous), { once: true })
  element.focus()
}
